import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Layers, ChevronDown, ChevronRight, Merge, Split, Filter } from "lucide-react";
import api from "../lib/api";
import { history } from "../lib/history";
import { deriveGroups, mergeGroups, splitGroup, rangeOf } from "../lib/aiGroups";

// "Joined positions from several pages": review what AI linked across pages, join or split groups (shared learning).
export function AiGroupsPanel({ review, groupFilter, setGroupFilter, t }) {
    const { items, setItems, projectId } = review;
    const groups = useMemo(() => deriveGroups(items), [items]);
    const [open, setOpen] = useState(true);
    const [sel, setSel] = useState([]);
    const multi = groups.filter((g) => g.pages.length > 1);
    const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
    const learn = (kind, gs) => {
        const body = { kind, labels: gs.map((g) => g.label || rangeOf(g)), pages: [...new Set(gs.flatMap((g) => g.pages))], ranges: gs.map(rangeOf) };
        const url = `/projects/${projectId}/ai-link-feedback`;
        history.attach({ label: "AI", undo: () => api.post(url, { ...body, undo: true }).catch(() => {}), redo: () => api.post(url, body).catch(() => {}) });
        return api.post(url, body).catch(() => {});
    };
    const doMerge = () => {
        const gs = groups.filter((g) => sel.includes(g.key));
        setItems((list) => mergeGroups(list, sel));
        learn("merge", gs);
        setSel([]); setGroupFilter(null);
        toast.success(t("ag_merged_toast"));
    };
    const doSplit = () => {
        const g = groups.find((x) => x.key === sel[0]);
        setItems((list) => splitGroup(list, g.key));
        learn("split", [g]);
        setSel([]); setGroupFilter(null);
        toast.success(t("ag_split_toast"));
    };
    const one = sel.length === 1 && groups.find((g) => g.key === sel[0]);
    return (
        <div data-testid="ai-groups-panel" className="rounded-lg border border-[#1e293b] bg-[#0b0f17]">
            <div className="flex items-center gap-2 px-2 h-9">
                <button data-testid="ai-groups-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-xs font-mono text-slate-200 hover:text-white">
                    {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    <Layers className="w-3.5 h-3.5 text-cyan-400" /> {t("ag_title")}
                    <span data-testid="ai-groups-multi-count" className="ml-1 px-1.5 rounded-full bg-cyan-500/15 text-cyan-300">{multi.length}</span>
                </button>
                <div className="ml-auto flex gap-1.5">
                    <button data-testid="ai-groups-merge" disabled={sel.length < 2} onClick={doMerge}
                        className="h-7 px-2 rounded text-xs border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40 flex items-center gap-1"><Merge className="w-3.5 h-3.5" /> {t("ag_merge")}{sel.length > 1 ? ` (${sel.length})` : ""}</button>
                    <button data-testid="ai-groups-split" disabled={!one || one.pages.length < 2} onClick={doSplit}
                        className="h-7 px-2 rounded text-xs border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 disabled:opacity-40 flex items-center gap-1"><Split className="w-3.5 h-3.5" /> {t("ag_split")}</button>
                </div>
            </div>
            {open && (
                <div className="max-h-40 overflow-auto px-2 pb-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-1.5">
                    {groups.map((g) => (
                        <div key={g.key} data-testid={`ai-group-${g.key}`}
                            className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs transition-colors ${sel.includes(g.key) ? "border-cyan-400 bg-cyan-500/10" : g.pages.length > 1 ? "border-cyan-500/30" : "border-[#1e293b]"}`}>
                            <input type="checkbox" data-testid={`ai-group-select-${g.key}`} checked={sel.includes(g.key)} onChange={() => toggle(g.key)} className="accent-cyan-400" />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                    <span data-testid={`ai-group-label-${g.key}`} className="font-mono font-semibold text-slate-100 truncate">{g.label || t("ag_no_label")}</span>
                                    {g.pages.length > 1 && <span className="px-1 rounded bg-cyan-500/20 text-cyan-200 text-[10px]">{t("ag_merged_badge")}</span>}
                                </div>
                                <div className="text-slate-400 font-mono truncate">
                                    <span data-testid={`ai-group-pages-${g.key}`}>{t("ag_pages")}: {g.pages.join(", ")}</span>
                                    {" · "}<span data-testid={`ai-group-range-${g.key}`}>{t("ag_positions")}: {rangeOf(g)}</span>
                                    {" · "}{g.ids.length} {t("ag_items")}
                                </div>
                            </div>
                            <button data-testid={`ai-group-filter-${g.key}`} title={t("ag_filter")} onClick={() => setGroupFilter(groupFilter === g.key ? null : g.key)}
                                className={`p-1 rounded hover:bg-white/10 ${groupFilter === g.key ? "text-cyan-300" : "text-slate-500"}`}><Filter className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                    {!groups.length && <div className="text-xs text-slate-500 py-2">{t("ag_none")}</div>}
                </div>
            )}
        </div>
    );
}
